import React, { createContext, useContext } from 'react';

import { stateCardSurfaceSx } from './stateCardSurface';

/**
 * VRG-720 — whether the access gate and the claim prompt render as IN-PAGE CARDS
 * or as a BOTTOM SHEET.
 *
 * ## This is a comparison harness, not a shipped feature
 *
 * Both presentations are built so the two can be looked at side by side before one
 * is chosen; `?present=sheet` on the certificate URL selects the sheet. Whichever
 * loses should be DELETED along with this file rather than left as a dead branch —
 * two presentations of the same surface is exactly the drift `working-conventions`
 * §71 is about, and a flag nobody flips is just an un-reviewed second codepath.
 *
 * ## The two are NOT interchangeable, and the gate is the harder case
 *
 * The card mount site in `CertificatePage` carries the original argument, and it is
 * a good one: the gate lands "under the item's own photograph and name, over a
 * page-worth of obscured content: the arrangement people already read as 'there is
 * something here, and this is what it costs'". A sheet's backdrop DIMS that obscured
 * content — which is the signal doing the persuading — so the sheet version is not
 * merely a relocation, it spends the thing the card was designed around.
 *
 * Two further asymmetries worth watching for while comparing:
 *
 * 1. **The gate is mandatory; a sheet affords dismissal.** The sheet below is
 *    therefore given `disableBackdropClick`, `disableSwipeToClose` and no grabber.
 *    That is the honest configuration, and it is also the thing to judge: a sheet
 *    that cannot be swiped away may read as a stuck control rather than a gate.
 * 2. **The gate has two text inputs.** Bottom sheet + focused input + iOS keyboard
 *    is a well-known trap the in-flow card avoids for free by simply scrolling.
 *    Check this on a real phone, not in the pane.
 *
 * The claim prompt has neither problem — it is optional, it already has a dismiss
 * (`onDismiss`, plus its own "View this item") — but it has a different one: it
 * ALREADY opens a `DrawerPopup` when you press "Claim ownership". As a card that is
 * card → sheet; as a sheet it becomes sheet → sheet, which spends the whole §52
 * two-drawer budget at step one and obliges everything after it to be a STEP inside
 * that second sheet rather than a third drawer.
 */
export type GatePresentation = 'card' | 'sheet';

const GatePresentationContext = createContext<GatePresentation>('card');

export const GatePresentationProvider = ({
  value,
  children,
}: {
  value: GatePresentation;
  children: React.ReactNode;
}) => (
  <GatePresentationContext.Provider value={value}>
    {children}
  </GatePresentationContext.Provider>
);

export const useGatePresentation = (): GatePresentation =>
  useContext(GatePresentationContext);

/**
 * The surface the gate's own root should paint.
 *
 * In the panel presentation it paints NOTHING: `GateSurface` is already the
 * surface — full-bleed, opaque, rounded at the top — and a bordered, shadowed
 * `background.paper` card sitting inside it would be two nested containers, which
 * reads as a mistake rather than as either presentation. The card's own `mb` goes
 * with it, since the panel owns its padding.
 */
export const useGateSurfaceSx = () =>
  useGatePresentation() === 'sheet' ? ({} as const) : stateCardSurfaceSx;

/*
 * ── WHY THERE IS NO MOBILE VARIANT HERE, HAVING TRIED ONE ────────────────────
 *
 * This briefly dropped the card's border, shadow, radius and padding below 600px
 * so the gate ran at the page gutter, on the argument that a 16px gutter plus 16px
 * of padding spends 64px of a 375px screen on the axis a form can least afford.
 *
 * It was reverted on sight, and the reason is one this file should have checked
 * BEFORE changing it: `stateCardSurface.ts` is not chrome, it is a NAMED FAMILY
 * with twelve consumers, and its own docblock is explicit that a state card
 * "sits ON the certificate, it is not part of it". The white + 1px hairline +
 * `elevation.card` recipe was chosen there specifically to match
 * `CertificatesTeaser`, so that "the state card [is] a member of the same family
 * instead of a one-off". Taking two of those twelve out of the family destroys
 * the only signal that separates a PENDING ACTION from permanent page content —
 * to buy 32px.
 *
 * Two near-misses are worth recording so they are not re-proposed:
 *
 * - A rule above the heading, in place of the card edge. It reads fine in
 *   isolation and makes the other eleven cards look wrong by comparison: the
 *   page then has two different ways of saying "a block starts here".
 * - Keeping the card but pulling it out to a narrower gutter than the rest of the
 *   page. Same defect as removing it, quieter — one card off the line every other
 *   card sits on.
 *
 * The width complaint that started this came from a full-bleed PANEL experiment,
 * not from the card. If it ever needs answering, the lever is the PAGE gutter for
 * everything at once, never this surface alone.
 */

/**
 * Whether the gate renders its OWN heading.
 *
 * TRUE in both presentations. It was false for the panel while that was built on
 * `DrawerPopup`, whose header carried the title — rendering one there too produced
 * the stacked-duplicate-heading defect `ScanTag` records from the gift flow ("two
 * of them were titled 'Gift', so the success screen appeared as a second identical
 * header sliding over the first"). The panel has no header of its own, so the
 * heading has to come back or the surface is untitled.
 *
 * Kept as a hook rather than deleted: it is the thing to flip if the panel ever
 * grows a header, and a `true` constant with this reasoning attached is cheaper to
 * re-read than to rediscover.
 */
export const useGateRendersOwnTitle = () => true;
